import { makeAutoObservable, runInAction } from "mobx";
import { t, translate } from "../../I18n";
import { CheckboxFieldController, ErrorBox, TextFieldController, } from "../../form";
import { NewsletterConsentModel, NewsletterEmailModel } from "../../ordering";
import { Tracking } from "../../tracking";
/**
 * Drives the newsletter signup widget. Deliberately separate from the
 * marketing-consent controllers - never touches order-time consent state.
 */
export class NewsletterSignupController {
    static create(params) {
        return new NewsletterSignupController({
            subscriptionService: params.subscriptionService,
            location: params.location,
            emailModel: new NewsletterEmailModel(""),
            consentRestaurantModel: new NewsletterConsentModel(false),
            consentRestaumaticModel: new NewsletterConsentModel(false),
        });
    }
    constructor(params) {
        this.mountedAt = Date.now();
        this._status = { type: "Editing" };
        this._resendStatus = { type: "Idle" };
        this.trackEmailFocused = () => {
            Tracking.trackEventForVisit(`newsletter_email_${this.location}`, {
                tag: "NewsletterEmailFocused",
                contents: { location: this.location },
            });
        };
        this.subscriptionService = params.subscriptionService;
        this.location = params.location;
        this.emailModel = params.emailModel;
        this.consentRestaurantModel = params.consentRestaurantModel;
        this.consentRestaumaticModel = params.consentRestaumaticModel;
        this.email = new TextFieldController({
            initialValue: this.emailModel.value,
            onUpdate: (value) => {
                this.emailModel.value = value;
            },
            errorBox: new ErrorBox({
                domainObjects: [this.emailModel],
                errorMessages: {
                    InvalidEmail: translate(t.validations.invalid_email),
                },
            }),
        });
        this.consentRestaurant = new CheckboxFieldController({
            initialValue: this.consentRestaurantModel.value,
            onUpdate: (value) => {
                this.consentRestaurantModel.value = value;
                this.updateGroupValue();
            },
            errorBox: new ErrorBox({
                domainObjects: [this.consentRestaurantModel],
                errorMessages: {
                    ConsentRequired: translate(t.newsletter.signup.consent.restaurant.required),
                },
            }),
        });
        this.consentRestaumatic = new CheckboxFieldController({
            initialValue: this.consentRestaumaticModel.value,
            onUpdate: (value) => {
                this.consentRestaumaticModel.value = value;
                this.updateGroupValue();
            },
        });
        this.consentGroup = new CheckboxFieldController({
            initialValue: this.consentRestaurantModel.value || this.consentRestaumaticModel.value,
            onUpdate: (value) => {
                this.consentRestaurant.change(value);
                this.consentRestaumatic.change(value);
            },
        });
        makeAutoObservable(this);
    }
    updateGroupValue() {
        this.consentGroup.changeWithoutUpdate(this.consentRestaurant.value || this.consentRestaumatic.value);
    }
    get isConsentGroupIndeterminate() {
        return this.consentRestaurant.value !== this.consentRestaumatic.value;
    }
    get status() {
        return this._status;
    }
    get isSubmitting() {
        return this._status.type === "Submitting";
    }
    get hasNetworkError() {
        return this._status.type === "NetworkError";
    }
    get isPending() {
        return this._status.type === "Pending";
    }
    /** The address the confirmation mail was sent to, once submitted. */
    get pendingEmail() {
        return this._status.type === "Pending" ? this._status.email : null;
    }
    /** Restaurant consent is mandatory; the Restaumatic S.A. one is optional. */
    get canSubmit() {
        return (this.email.isValid && this.consentRestaurant.isValid && !this.isSubmitting);
    }
    get isResending() {
        return this._resendStatus.type === "Sending";
    }
    get hasResent() {
        return this._resendStatus.type === "Sent";
    }
    get hasResendFailed() {
        return this._resendStatus.type === "Failed";
    }
    get canResend() {
        return this.isPending && !this.isResending;
    }
    trackBandShown() {
        Tracking.trackEventForVisit(`newsletter_shown_${this.location}`, {
            tag: "NewsletterBandShown",
            contents: { location: this.location },
        });
    }
    async submit(website) {
        // Fired unconditionally, even if canSubmit below rejects it - this tracks
        // the click itself, not a completed signup (that's NewsletterSubscribed).
        Tracking.trackEvent({
            tag: "NewsletterSubmitted",
            contents: { location: this.location },
        });
        this.email.blur();
        this.consentRestaurant.blur();
        this.consentRestaumatic.blur();
        if (!this.canSubmit) {
            return;
        }
        const email = this.emailModel.email;
        runInAction(() => {
            this._status = { type: "Submitting" };
        });
        const { success, trackingId } = await this.subscriptionService.subscribe({
            email,
            marketingConsent: this.consentRestaurantModel.value,
            marketingRestaumaticConsent: this.consentRestaumaticModel.value,
            website,
            elapsedMs: Date.now() - this.mountedAt,
        });
        runInAction(() => {
            this._status = success
                ? { type: "Pending", email: email.value }
                : { type: "NetworkError" };
        });
        if (success) {
            Tracking.trackEvent({
                tag: "NewsletterSubscribed",
                contents: { location: this.location, trackingId },
            });
        }
    }
    /** Re-send the confirmation mail; only meaningful from the pending state. */
    async resend() {
        if (!this.canResend) {
            return;
        }
        runInAction(() => {
            this._resendStatus = { type: "Sending" };
        });
        const success = await this.subscriptionService.resend({
            email: this.emailModel.email,
        });
        runInAction(() => {
            this._resendStatus = success ? { type: "Sent" } : { type: "Failed" };
        });
    }
}
